import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useInView } from "react-intersection-observer";
import Modal from "react-modal";
import closeModal from "../../assets/project-media/close.svg";
import "./openSourceProject.css";

const OpenSourceProject = ({ url, color, id }) => {
  // Parse GitHub URL to extract owner, repo, and author
  const parseGitHubUrl = (githubUrl) => {
    try {
      const urlObj = new URL(githubUrl);
      const pathParts = urlObj.pathname.split('/').filter(Boolean);
      const owner = pathParts[0];
      const repo = pathParts[1];
      
      // Extract author from query params
      const searchParams = new URLSearchParams(urlObj.search);
      const query = searchParams.get('q') || '';
      const authorMatch = query.match(/author[:%]([^\s+&]+)/);
      const author = authorMatch ? authorMatch[1] : '';
      
      return { owner, repo, author };
    } catch (error) {
      console.error('Error parsing GitHub URL:', error);
      return { owner: '', repo: '', author: '' };
    }
  };

  const { owner, repo, author } = parseGitHubUrl(url);
  const [ref, inView] = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  const variants = {
    hidden: { x: id % 2 === 0 ? "10vw" : "-10vw", opacity: 0 },
    visible: { x: 0, opacity: 1 },
  };

  Modal.setAppElement("#root");

  const [showModal, setShowModal] = useState(false);
  const [prData, setPrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleOpenModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

  useEffect(() => {
    if (owner && repo && author) {
      fetchPRData();
    }
  }, [url]);

  const fetchPRData = async () => {
    const cacheKey = `github_prs_${owner}_${repo}_${author}`;
    const cached = localStorage.getItem(cacheKey);
    const cacheTime = localStorage.getItem(`${cacheKey}_time`);

    // Cache for 1 hour
    if (cached && cacheTime && Date.now() - parseInt(cacheTime) < 3600000) {
      setPrData(JSON.parse(cached));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(
        `https://api.github.com/search/issues?q=is:pr+is:merged+author:${author}+repo:${owner}/${repo}&per_page=100&sort=updated&order=desc`
      );

      if (!response.ok) {
        throw new Error(`GitHub API error: ${response.status}`);
      }

      const data = await response.json();
      const prs = data.items;

      // Fetch additional details for each PR (to get lines changed)
      const prDetails = await Promise.all(
        prs.slice(0, 20).map(async (pr) => {
          try {
            const prResponse = await fetch(pr.pull_request.url);
            if (!prResponse.ok) return null;
            const prData = await prResponse.json();
            return {
              number: pr.number,
              title: pr.title,
              mergedAt: prData.merged_at,
              url: pr.html_url,
              additions: prData.additions,
              deletions: prData.deletions,
              changedFiles: prData.changed_files,
              comments: pr.comments,
            };
          } catch (err) {
            console.error(`Error fetching PR #${pr.number}:`, err);
            return null;
          }
        })
      );

      const validPrDetails = prDetails.filter((pr) => pr !== null);

      const aggregatedData = {
        totalPRs: data.total_count,
        prs: validPrDetails,
        totalAdditions: validPrDetails.reduce((sum, pr) => sum + pr.additions, 0),
        totalDeletions: validPrDetails.reduce((sum, pr) => sum + pr.deletions, 0),
        totalChangedFiles: validPrDetails.reduce((sum, pr) => sum + pr.changedFiles, 0),
        lastMerged: validPrDetails[0]?.mergedAt || null,
      };

      localStorage.setItem(cacheKey, JSON.stringify(aggregatedData));
      localStorage.setItem(`${cacheKey}_time`, Date.now().toString());
      setPrData(aggregatedData);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching PR data:", err);
      setError(err.message);
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return num?.toLocaleString() || "0";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <motion.div
      ref={ref}
      className="col-sm-12 col-lg-6"
      variants={variants}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      transition={{ duration: 0.4, ease: "easeInOut" }}
    >
      <div
        style={{ backgroundColor: color }}
        className="projectCard openSourceCard d-flex align-items-center justify-content-between p-5"
        onClick={handleOpenModal}
      >
        {loading ? (
          <div className="loadingState">
            <p>Loading {repo}...</p>
          </div>
        ) : error ? (
          <div className="errorState">
            <h3 className="projectTitle">{repo}</h3>
            <p className="errorText">Failed to load data</p>
          </div>
        ) : (
          <>
            <div className="textWrap col-6 d-flex flex-column justify-content-center align-items-start">
              <p className="repoOwner">{owner}</p>
              <h3 className="projectTitle">{repo}</h3>
              <div className="statsGrid">
                <div className="statItem">
                  <span className="statValue">{formatNumber(prData?.totalPRs)}</span>
                  <span className="statLabel">Merged PRs</span>
                </div>
                <div className="statItem">
                  <span className="statValue">
                    +{formatNumber(prData?.totalAdditions)} / -
                    {formatNumber(prData?.totalDeletions)}
                  </span>
                  <span className="statLabel">Lines Changed</span>
                </div>
                <div className="statItem">
                  <span className="statValue">{formatNumber(prData?.totalChangedFiles)}</span>
                  <span className="statLabel">Files Modified</span>
                </div>
              </div>
              <span className="viewWork">View Contributions &#8594;</span>
            </div>
            <div className="iconContainer col-6 d-flex align-items-center justify-content-center">
              <svg
                className="githubIcon"
                viewBox="0 0 24 24"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.17 6.839 9.49.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.463-1.11-1.463-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.336 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.167 22 16.418 22 12c0-5.523-4.477-10-10-10z" />
              </svg>
            </div>
          </>
        )}
      </div>

      <Modal
        isOpen={showModal}
        onRequestClose={handleCloseModal}
        style={{
          content: {
            backgroundColor: "#faf8f3",
            padding: "3%",
            display: "flex",
            flexDirection: "column",
            width: "90%",
            maxWidth: "1200px",
            height: "80vh",
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            zIndex: "999",
            overflowY: "auto",
            border: "2px solid #8d6e63",
            borderRadius: "12px",
          },
          overlay: { zIndex: 1000, backgroundColor: "rgba(62, 39, 35, 0.85)" },
        }}
      >
        <img
          src={closeModal}
          className="closeMenu closeModal"
          onClick={handleCloseModal}
          alt="Close"
        />
        <div className="modalHeader">
          <h3 className="modalTitle">
            {owner}/{repo}
          </h3>
          <p className="modalSubtitle">Open Source Contributions</p>
        </div>

        {prData && (
          <>
            <div className="modalStatsSection">
              <div className="modalStat">
                <h4>{formatNumber(prData.totalPRs)}</h4>
                <p>Total Merged PRs</p>
              </div>
              <div className="modalStat">
                <h4>+{formatNumber(prData.totalAdditions)}</h4>
                <p>Lines Added</p>
              </div>
              <div className="modalStat">
                <h4>-{formatNumber(prData.totalDeletions)}</h4>
                <p>Lines Deleted</p>
              </div>
              <div className="modalStat">
                <h4>{formatNumber(prData.totalChangedFiles)}</h4>
                <p>Files Modified</p>
              </div>
            </div>

            <div className="prListSection">
              <h4 className="prListTitle">Recent Pull Requests</h4>
              {prData.prs.map((pr) => (
                <div key={pr.number} className="prCard">
                  <div className="prHeader">
                    <a
                      href={pr.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="prTitle"
                      onClick={(e) => e.stopPropagation()}
                    >
                      #{pr.number} {pr.title}
                    </a>
                    <span className="prDate">{formatDate(pr.mergedAt)}</span>
                  </div>
                  <div className="prStats">
                    <span className="prStat additions">+{formatNumber(pr.additions)}</span>
                    <span className="prStat deletions">-{formatNumber(pr.deletions)}</span>
                    <span className="prStat files">{pr.changedFiles} files</span>
                    {pr.comments > 0 && (
                      <span className="prStat comments">{pr.comments} comments</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <button
              className="btn githubLink"
              onClick={() =>
                window.open(
                  `https://github.com/${owner}/${repo}/pulls?q=is:pr+is:merged+author:FelixVaughan`,
                  "_blank"
                )
              }
            >
              View All on GitHub
            </button>
          </>
        )}
      </Modal>
    </motion.div>
  );
};

export default OpenSourceProject;

